// client/src/Wallets.jsx
import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Select from "react-select";
import "./Style.css";
import GameSessionHeader from "./GameSessionHeader";
import { formatTransactionCode } from "./utils/transactionUtils";

import {
  FaTimes,
  FaInfo,
  FaCheck,
  FaBell,
  FaRegTrashAlt,
  FaPlay,
  FaPause,
  FaDollarSign,
} from "react-icons/fa";

export default function Wallets() {
  const { cardroom } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const permission = localStorage.getItem("permission");
  const dealerMode = Number(localStorage.getItem("dealer_mode") || 0);

  // ============================================================================
  // CORE STATE
  // ============================================================================
  const [wallets, setWallets] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [costs, setCosts] = useState([]);
  const [currSession, setCurrSession] = useState(null);

  // ============================================================================
  // SESSION INFO STATE
  // ============================================================================
  const [cashBag, setCashBag] = useState(null);
  const [chipsOnTables, setChipsOnTables] = useState(0);
  const [sessionRake, setSessionRake] = useState(0);
  const [sessionTips, setSessionTips] = useState(0);
  const [badBeatAmount, setBadBeatAmount] = useState(0);
  const [badBeatEnabled, setBadBeatEnabled] = useState(false);
  const [lastReset, setLastReset] = useState("");
  const [lastEnd, setLastEnd] = useState("");
  const [tableCount, setTableCount] = useState(0);
  const [totalCredit, setTotalCredit] = useState(0);
  const [unpaidCosts, setUnpaidCosts] = useState([]);

  // ============================================================================
  // UI STATE
  // ============================================================================
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [showCostsModal, setShowCostsModal] = useState(false);
  // Add these lines with other state declarations:
  const [showPayoutsModal, setShowPayoutsModal] = useState(false);
  const [allPayouts, setAllPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showStartSessionWarning, setShowStartSessionWarning] = useState(false);
  const [showEndSessionWarning, setShowEndSessionWarning] = useState(false);
  const [showCashResetWarning, setShowCashResetWarning] = useState(false);

  // ============================================================================
  // FORM STATE - Cash Refill
  // ============================================================================
  const [refillAmount, setRefillAmount] = useState("");
  const [refillWalletId, setRefillWalletId] = useState("");

  const [payingWallet, setPayingWallet] = useState(null);
  const [payDirection, setPayDirection] = useState("to_wallet"); // "to_wallet", "from_wallet", or "wallet_to_wallet"
  const [payWalletAmount, setPayWalletAmount] = useState("");
  const [payWalletNotes, setPayWalletNotes] = useState("");
  const [receiverWalletId, setReceiverWalletId] = useState("");
  const payWalletAmountInputRef = useRef(null);

  // ============================================================================
  // FORM STATE - Add Wallet
  // ============================================================================
  const [newWalletName, setNewWalletName] = useState("");

  // ============================================================================
  // FORM STATE - Costs
  // ============================================================================
  const [costAmount, setCostAmount] = useState("");
  const [costDescription, setCostDescription] = useState("");
  const [costMethod, setCostMethod] = useState("");
  const [costReceiver, setCostReceiver] = useState("");
  const [costPayLater, setCostPayLater] = useState(false);

  // ============================================================================
  // FORM STATE - Worker Transactions
  // ============================================================================
  const [selectedWorkerId, setSelectedWorkerId] = useState("");
  const [workerAmount, setWorkerAmount] = useState("");
  const [workerMethod, setWorkerMethod] = useState("");
  const [workerReceiver, setWorkerReceiver] = useState("");

  // ============================================================================
  // MODAL STATE - Cash Bag Transactions
  // ============================================================================
  const [cashBagTransactions, setCashBagTransactions] = useState([]);
  const [showCashBagModal, setShowCashBagModal] = useState(false);

  // ============================================================================
  // MODAL STATE - Wallet Transactions
  // ============================================================================
  const [selectedWalletId, setSelectedWalletId] = useState(null);
  const [selectedWalletTransactions, setSelectedWalletTransactions] = useState(
    []
  );

  // ============================================================================
  // EFFECT: Auth & Axios Interceptor
  // ============================================================================
  useEffect(() => {
    if (dealerMode === 2) {
      navigate(`/${cardroom}/dealer_dashboard`);
      return;
    }

    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem("token");
          navigate(`/${cardroom}/`);
        }
        return Promise.reject(error);
      }
    );

    return () => axios.interceptors.response.eject(interceptor);
  }, [cardroom, dealerMode,navigate]);

  // ============================================================================
  // EFFECT: Initial Data Load
  // ============================================================================
  useEffect(() => {
    if (!token) {
      navigate(`/`);
      return;
    }

    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

    async function loadData() {
      try {
        const { data } = await axios.get(`/${cardroom}/api/cardroom`);
        setCurrSession(data.active);
        setCashBag(data.cash);
        setChipsOnTables(data.chipsOnTables);
        setSessionRake(data.sessionRake);
        setSessionTips(data.sessionTips);
        setBadBeatAmount(data.bad_beat_amount || 0);
        setBadBeatEnabled(data.bad_beat === 1 || data.badBeat === 1);
        setLastReset(data.lastSessionStart);
        setLastEnd(data.lastSessionEnd);
        setTotalCredit(data.total_credit);
        const { data: unpaidCostsData } = await axios.get(`/${cardroom}/api/unpaid_costs`);
        setUnpaidCosts(unpaidCostsData);

        const { data: walletsData } = await axios.get(
          `/${cardroom}/api/wallets`
        );
        setWallets(walletsData);

        const { data: workersData } = await axios.get(
          `/${cardroom}/api/service_staff`
        );
        setWorkers(workersData);

        const {
          data: { count },
        } = await axios.get(`/${cardroom}/api/tables/count`);
        setTableCount(count);
      } catch (err) {
        console.error("Load data failed", err);
        navigate(`/`);
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [token, cardroom, navigate]);

  // ============================================================================
  // EFFECT: Prevent body scroll when modal is open
  // ============================================================================
  const isAnyModalOpen =
    showCashBagModal ||
    selectedWalletId !== null ||
    showCostsModal ||
    payingWallet !== null ||
    showPayoutsModal ||
    showStartSessionWarning ||
    showEndSessionWarning ||
    showCashResetWarning;

  useEffect(() => {
    document.body.style.overflow = isAnyModalOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isAnyModalOpen]);

  const preventNumberInputWheel = (e) => {
    e.preventDefault();
    e.currentTarget.blur();
  };

  useEffect(() => {
    if (!payingWallet) return;
    requestAnimationFrame(() => {
      payWalletAmountInputRef.current?.focus();
      payWalletAmountInputRef.current?.select();
    });
  }, [payingWallet]);

  // ============================================================================
  // HANDLERS: Session Management
  // ============================================================================
  const confirmStartSession = async () => {
    if (isSubmitting) return;

    try {
      setIsSubmitting(true);
      await axios.post(`/${cardroom}/api/session/start`);

      const { data } = await axios.get(`/${cardroom}/api/cardroom`);
      setCurrSession(data.active);
      setChipsOnTables(data.chipsOnTables);
      setSessionRake(0);
      setSessionTips(0);
      setBadBeatAmount(data.bad_beat_amount || 0);
      setBadBeatEnabled(data.bad_beat === 1 || data.badBeat === 1);
      setShowStartSessionWarning(false);
    } catch (err) {
      console.error("Start session failed", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const startSession = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (Number(cashBag || 0) !== 0) {
      setShowStartSessionWarning(true);
      return;
    }

    await confirmStartSession();
  };

  const confirmEndSession = async () => {
    try {
      setIsSubmitting(true);
      await axios.post(`/${cardroom}/api/tenant/end_session`);

      const { data } = await axios.get(`/${cardroom}/api/cardroom`);
      setCurrSession(data.active);
      setCashBag(data.cash);
      setChipsOnTables(data.chipsOnTables);
      setShowEndSessionWarning(false);
    } catch (err) {
      console.error("End session failed", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const endSession = async () => {
    if (tableCount !== 0) {
      alert("Can't end session, there are still active tables!");
      return;
    }

    if (Number(cashBag || 0) !== 0 || Number(chipsOnTables || 0) !== 0) {
      setShowEndSessionWarning(true);
      return;
    }

    await confirmEndSession();
  };

  // ============================================================================
  // HANDLERS: Cash Bag Management
  // ============================================================================
  const verifyCashAmount = async () => {
    try {
      await axios.post(`/${cardroom}/api/cash_verify`);
      setNotifications((prev) => [
        ...prev,
        { id: Date.now(), message: `Cash bag verified at $${cashBag}` },
      ]);
    } catch (err) {
      console.error("Verify cash failed", err);
    }
  };

  const clearCashBag = async () => {
    try {
      await axios.post(`/${cardroom}/api/cash_reset`);
      setCashBag(0);
      setNotifications((prev) => [
        ...prev,
        { id: Date.now(), message: "Cash bag cleared" },
      ]);
    } catch (err) {
      console.error("Clear cash failed", err);
    }
  };

  const resetNegativeCashBag = async () => {
    try {
      setIsSubmitting(true);
      await axios.post(`/${cardroom}/api/cash_negative_reset`);
      setCashBag(0);
      setShowCashResetWarning(false);
      setNotifications((prev) => [
        ...prev,
        { id: Date.now(), message: "Cash bag reset" },
      ]);
    } catch (err) {
      console.error("Reset cash failed", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const addCashRefill = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    try {
      setIsSubmitting(true);
      const amt = parseInt(refillAmount, 10);

      await axios.post(`/${cardroom}/api/${refillWalletId}/cash_refill`, {
        amount: amt,
      });

      const { data } = await axios.get(`/${cardroom}/api/wallets`);
      setWallets(data);

      setCashBag((prev) => (prev || 0) + amt);
      setRefillAmount("");
      setRefillWalletId("");
    } catch (err) {
      console.error("Cash refill failed", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const listCashBagTransactions = async () => {
    try {
      const { data } = await axios.get(`/${cardroom}/api/cashbag_transactions`);
      setCashBagTransactions(data);
      setShowCashBagModal(true);
    } catch (err) {
      console.error("List cash bag transactions failed", err);
    }
  };

  const listCosts = async () => {
    try {
      const { data: costsData } = await axios.get(`/${cardroom}/api/costs`);
      setCosts(costsData);
      setShowCostsModal(true);
    } catch (err) {
      console.error("List costs failed", err);
    }
  };

  const listAllPayouts = async () => {
    try {
      const { data } = await axios.get(`/${cardroom}/api/staff_only/payouts`);
      setAllPayouts(data);
      setShowPayoutsModal(true);
    } catch (err) {
      console.error("List all payouts failed", err);
    }
  };

  // ============================================================================
  // HANDLERS: Wallet Management
  // ============================================================================
  const addWallet = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (!newWalletName.trim()) {
      alert("Enter a wallet name");
      return;
    }

    try {
      setIsSubmitting(true);
      const { data: wallet } = await axios.post(`/${cardroom}/api/wallets`, {
        name: newWalletName.trim(),
      });

      setWallets((prev) => [...prev, wallet]);
      setNewWalletName("");
    } catch (err) {
      alert(err.response?.data?.error || "Could not add wallet");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleWalletPayment = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    
    if (payDirection === "wallet_to_wallet" && !receiverWalletId) {
       alert("Please select a receiving wallet.");
       return;
    }

    try {
      setIsSubmitting(true);

      // Determine the actual amount based on direction
      const actualAmount =
        payDirection === "to_wallet"
          ? parseFloat(payWalletAmount)
          : -parseFloat(payWalletAmount);

      await axios.post(`/${cardroom}/api/wallet_payment`, {
        walletId: payingWallet.id,
        amount: actualAmount,
        notes: payWalletNotes.trim(),
        receiverWalletId: payDirection === "wallet_to_wallet" ? parseInt(receiverWalletId) : null,
      });

      setWallets((prev) => 
        prev.map((w) => {
          if (w.id === payingWallet.id) {
             return { ...w, digital: w.digital + actualAmount };
          }
          if (payDirection === "wallet_to_wallet" && w.id === parseInt(receiverWalletId)) {
             return { ...w, digital: w.digital - actualAmount };
          }
          return w;
        })
      );

      // Close modal and reset
      setPayingWallet(null);
      setPayDirection("to_wallet");
      setPayWalletAmount("");
      setPayWalletNotes("");
      setReceiverWalletId("");

      const directionText = payDirection === "wallet_to_wallet" 
          ? `to another wallet` 
          : (payDirection === "to_wallet" ? "to" : "from");

    } catch (err) {
      console.error("Wallet payment failed", err);
      alert(err.response?.data?.error || "Could not process payment");
    } finally {
      setIsSubmitting(false);
    }
  };

  const listWalletTransactions = async (walletId) => {
    setSelectedWalletId(walletId);
    try {
      const { data } = await axios.get(
        `/${cardroom}/api/wallets/${walletId}/transactions`
      );
      setSelectedWalletTransactions(data);
    } catch (err) {
      console.error("List wallet transactions failed", err);
    }
  };

  const resetWallet = async (walletId) => {
    try {
      await axios.post(`/${cardroom}/api/tenant/walletsreset/${walletId}`);
      const { data } = await axios.get(`/${cardroom}/api/wallets`);
      setWallets(data);
    } catch (err) {
      console.error("Reset wallet failed", err);
    }
  };

  // ============================================================================
  // HANDLERS: Costs
  // ============================================================================
  const addCost = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    try {
      setIsSubmitting(true);
      const effectiveMethod = costPayLater ? "credit" : costMethod;
      await axios.post(`/${cardroom}/api/costs`, {
        amount: parseFloat(costAmount),
        notes: costDescription.trim(),
        method: effectiveMethod,
        walletId: costReceiver || null,
      });

      if (effectiveMethod === "cash") {
        setCashBag((prev) => (prev || 0) - parseFloat(costAmount));
      } else if (effectiveMethod === "credit") {
        const { data: unpaidData } = await axios.get(`/${cardroom}/api/unpaid_costs`);
        setUnpaidCosts(unpaidData);
      } else if (costReceiver) {
        const { data } = await axios.get(`/${cardroom}/api/wallets`);
        setWallets(data);
      }

      setCostAmount("");
      setCostDescription("");
      setCostMethod("");
      setCostReceiver("");
      setCostPayLater(false);
    } catch (err) {
      console.error("Add cost failed", err);
      alert(err.response?.data?.error || "Could not add cost");
    } finally {
      setIsSubmitting(false);
    }
  };

  const deleteUnpaidCost = async (id) => {
    try {
      await axios.delete(`/${cardroom}/api/unpaid_costs/${id}`);
      const { data } = await axios.get(`/${cardroom}/api/unpaid_costs`);
      setUnpaidCosts(data);
    } catch (err) {
      console.error("Delete unpaid cost failed", err);
    }
  };

  const [payingUnpaidId, setPayingUnpaidId] = useState(null);
  const [payUnpaidMethod, setPayUnpaidMethod] = useState("");
  const [payUnpaidWalletId, setPayUnpaidWalletId] = useState("");

  const payUnpaidCost = async (unpaidId) => {
    if (!payUnpaidMethod) return;
    if (payUnpaidMethod !== "cash" && !payUnpaidWalletId) return;

    try {
      setIsSubmitting(true);
      await axios.post(`/${cardroom}/api/unpaid_costs/pay`, {
        id: unpaidId,
        method: payUnpaidMethod,
        walletId: payUnpaidWalletId || null,
      });

      // Refresh everything
      const { data: unpaidData } = await axios.get(
        `/${cardroom}/api/unpaid_costs`
      );
      setUnpaidCosts(unpaidData);

      const { data: infoData } = await axios.get(`/${cardroom}/api/cardroom`);
      setCashBag(infoData.cash);

      const { data: walletsData } = await axios.get(`/${cardroom}/api/wallets`);
      setWallets(walletsData);

      setPayingUnpaidId(null);
      setPayUnpaidMethod("");
      setPayUnpaidWalletId("");
    } catch (err) {
      console.error("Pay unpaid cost failed", err);
      alert(err.response?.data?.error || "Could not pay cost");
    } finally {
      setIsSubmitting(false);
    }
  };


  // ============================================================================
  // HANDLERS: Worker Transactions
  // ============================================================================
  const addWorkerTransaction = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    try {
      setIsSubmitting(true);
      await axios.post(`/${cardroom}/api/service_staff_transaction`, {
        staffId: selectedWorkerId,
        amount: parseFloat(workerAmount),
        credit: workerMethod === "credit",
        method: workerMethod === "credit" ? null : workerMethod,
        walletId: ["cash", "house"].includes(workerMethod)
          ? null
          : workerReceiver || null,
      });

      if (workerMethod === "cash") {
        setCashBag((prev) => (prev || 0) - parseFloat(workerAmount));
      } else if (workerReceiver) {
        const { data } = await axios.get(`/${cardroom}/api/wallets`);
        setWallets(data);
      }

      setChipsOnTables((prev) => prev - parseFloat(workerAmount));

      setSelectedWorkerId("");
      setWorkerAmount("");
      setWorkerMethod("");
      setWorkerReceiver("");
    } catch (err) {
      console.error("Add worker transaction failed", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // ============================================================================
  // COMPUTED VALUES
  // ============================================================================
  const totalWalletBalance = wallets.reduce((sum, w) => {
    return sum + (w.digital || 0);
  }, 0);

  const isValidRefillInt =
    Number.isInteger(Number(refillAmount)) && Number(refillAmount) > 0;

  const isWalletNameTaken = wallets.some(
    (w) => w.name.trim().toLowerCase() === newWalletName.trim().toLowerCase()
  );

  const deleteTransaction = async (
    transactionId,
    code,
    type,
    playerId,
    amount,
    receiver_name = null
  ) => {
    // Map display names for confirmation
    const typeDisplayMap = {
      'refill': 'cash refill',
      'clear': 'cash bag reset',
      'verify': 'cash verification',
      'cost': 'cost',
      'staff_payout': 'staff payout',
      'wallet_payment': 'wallet payment'
    };
    
    const typeDisplay = typeDisplayMap[type] || type;
    
  
    setIsSubmitting(true);
  
    try {
      // Find wallet if receiver_name is passed
      let computedWalletId = null;
      if (receiver_name) {
        const w = wallets.find((w) => w.name === receiver_name);
        if (w) computedWalletId = w.id;
      }

      // Delete the transaction
      await axios.delete(`/${cardroom}/api/deleteTransaction`, {
        data: {
          id: transactionId,
          code: code,
          player_id: playerId,
          amount: amount,
          wallet_id: computedWalletId,
        },
    });

    // ========================================================================
    // MANUAL STATE UPDATES - No API calls needed!
    // ========================================================================

    // Remove from cash bag transaction list
    setCashBagTransactions((prev) => 
      prev.filter((tx) => tx.id !== transactionId)
    );

    // Remove from selected wallet transaction list
    setSelectedWalletTransactions((prev) => 
      prev.filter((tx) => tx.id !== transactionId)
    );

    // Update state based on transaction type
    switch (code) {
      case 32: // cost
        if (receiver_name) {
          setWallets((prev) =>
            prev.map((w) =>
               w.name === receiver_name
                ? { ...w, digital: Number(w.digital) + Number(amount) }
                : w
            )
          );
        } else {
          setCashBag((prev) => Number(prev || 0) + Number(amount));
        }
        
        setCosts((prev) => prev.filter((c) => c.id !== transactionId));
        break;

      case 36: // wallet_payment
        setWallets((prev) =>
          prev.map((w) =>
             w.name === receiver_name
              ? { ...w, digital: Number(w.digital) - Number(amount) }
              : w
          )
        );
        
        setSelectedWalletTransactions((prev) => 
          prev.filter((tx) => tx.id !== transactionId)
        );
        break;

      case 33: // refill
        setWallets((prev) =>
          prev.map((w) =>
             w.name === receiver_name
              ? { ...w, digital: Number(w.digital) + Number(amount) }
              : w
          )
        );
        setCashBag((prev) => Number(prev || 0) - Number(amount));
        break;
      
      case 35: // clear
        setCashBag((prev) => Number(prev || 0) + Number(amount));
        break;

      case 37: // negative reset
        setCashBag((prev) => Number(prev || 0) + Number(amount));
        break;

      case 34: // verify
        break;
        
      case 25: // staff credit
        setChipsOnTables((prev) => Number(prev) + Number(amount));
        setAllPayouts((prev) => prev.filter((p) => p.id !== transactionId));
        break;
        
      case 26: // staff method
        setChipsOnTables((prev) => Number(prev) + Number(amount));
        if (receiver_name) {
          setWallets((prev) =>
            prev.map((w) =>
               w.name === receiver_name
                ? { ...w, digital: Number(w.digital) + Number(amount) }
                : w
            )
          );
        }
        setAllPayouts((prev) => prev.filter((p) => p.id !== transactionId));
        break;
        
      case 27: // staff cash
        setChipsOnTables((prev) => Number(prev) + Number(amount));
        setCashBag((prev) => Number(prev || 0) + Number(amount));
        setAllPayouts((prev) => prev.filter((p) => p.id !== transactionId));
        break;
        
      case 28: // staff payout method
        if (receiver_name) {
          setWallets((prev) =>
            prev.map((w) =>
               w.name === receiver_name
                ? { ...w, digital: Number(w.digital) + Number(amount) }
                : w
            )
          );
        }
        setAllPayouts((prev) => prev.filter((p) => p.id !== transactionId));
        break;

      case 29: // staff payout cash
        setCashBag((prev) => Number(prev || 0) + Number(amount));
        setAllPayouts((prev) => prev.filter((p) => p.id !== transactionId));
        break;

      default:
        console.warn('Unknown transaction code:', code);
    }


  } catch (err) {
    console.error("Error deleting transaction:", err);
    const errorMsg = err.response?.data?.error || "Failed to delete transaction";
    alert(errorMsg);
    
    // Show error notification
    setNotifications((prev) => [
      ...prev,
      { 
        id: Date.now(), 
        message: `Error: ${errorMsg}` 
      },
    ]);
  } finally {
    setIsSubmitting(false);
  }
};

  // ============================================================================
  // LOADING STATE
  // ============================================================================

  return (
    <div className="game">
      <GameSessionHeader
        activePage="wallets"
        cardroom={cardroom}
        currSession={currSession}
        dealerMode={dealerMode}
        permission={permission}
        title="Session Manager"
      />

      {/* Notifications */}
      {notifications.length > 0 && (
        <div className="game-notifications">
          {notifications.map((n) => (
            <div key={n.id} className="game-notification">
              <FaBell className="game-notification__icon" />
              <span className="game-notification__message">{n.message}</span>
              <button
                className="game-notification__close"
                onClick={() =>
                  setNotifications((prev) => prev.filter((x) => x.id !== n.id))
                }
              >
                <FaTimes />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="game-container">
        {loading ? (
          <div className="loading-container" style={{ padding: "4rem", textAlign: "center", color: "white" }}>
            <h2 style={{ marginBottom: "1.5rem", fontWeight: "700" }}>Gathering Data...</h2>
            <div style={{ display: "flex", justifyContent: "center" }}>
              <div className="spinner" style={{ border: "4px solid rgba(255,255,255,0.1)", borderTop: "4px solid #6366f1", borderRadius: "50%", width: "50px", height: "50px", animation: "spin 1s linear infinite" }}></div>
            </div>
            <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
          </div>
        ) : (
          <>
        {/* Session Info Section - Full Width */}
        <section className="game-section">
          <h2 className="game-section__title">Session Information</h2>

          <div className="wallets-session-info">
            {/* Left Column - Session Control */}
            <div className="wallets-session-card">
              {currSession ? (
                <>
                  <button
                    className="game-btn game-btn--primary"
                    disabled={unpaidCosts.length > 0 || tableCount > 0}
                    onClick={endSession}
                    title={tableCount > 0 ? "All tables must be closed" : unpaidCosts.length > 0 ? "All unpaid costs must be paid off" : "End Session"}
                    style={{
                      width: "auto",
                      minWidth: "200px",
                      marginBottom: "1rem",
                    }}
                  >
                    <FaPause style={{ marginRight: "0.5rem" }} />
                    End Session
                  </button>

                  {lastReset && (
                    <p
                      className="game-text-secondary"
                      style={{ fontSize: "0.875rem" }}
                    >
                      Session Started:{" "}
                      {new Date(lastReset).toLocaleString(undefined, {
                        month: "numeric",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </p>
                  )}
                  {badBeatEnabled && (
<div
  style={{
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
    gap: ".5rem",
    marginTop: "0.5rem"
  }}
>
  <span className="game-text-secondary">
    Current Bad Beat:
  </span>
  <span
    className="game-text-primary"
    style={{ fontWeight: 600 }}
  >
    ${badBeatAmount}
  </span>
</div>
                  )}
                </>
              ) : (
                <>
                  <button
                    className="game-btn game-btn--primary"
                    onClick={startSession}
                    style={{
                      width: "auto",
                      minWidth: "200px",
                      marginBottom: "1rem",
                    }}
                    disabled={isSubmitting}
                  >
                    <FaPlay style={{ marginRight: "0.5rem" }} />
                    Start Session
                  </button>
                  {lastEnd && (
                    <p
                      className="game-text-secondary"
                      style={{ fontSize: "0.875rem" }}
                    >
                      Session Ended:{" "}
                      {new Date(lastEnd).toLocaleString(undefined, {
                        month: "numeric",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </p>
                  )}
                  {badBeatEnabled && (
<div
  style={{
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
    gap: ".5rem",
    marginTop: "0.5rem"
  }}
>
  <span className="game-text-secondary">
    Current Bad Beat:
  </span>
  <span
    className="game-text-primary"
    style={{ fontWeight: 600 }}
  >
    ${badBeatAmount}
  </span>
</div>
                  )}
                </>
              )}
            </div>

            {/* Middle Column - Cash Bag */}
            <div className="wallets-session-card"
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <span
                style={{
                  margin: "0 0 1rem 0",
                  display: "flex",
                  alignItems: "center",
                  gap: "1rem",
                }}
              >
                <h3 style={{ margin: 0, fontSize: "1rem", fontWeight: 600 }}>
                  <span
                    className={cashBag < 0 ? "game-text-danger" : "game-text-primary"}
                  >
                    Cash Bag: ${cashBag != null ? cashBag : 0}
                  </span>
                </h3>

                <button
                  className="game-icon-btn game-icon-btn--info"
                  onClick={listCashBagTransactions}
                  title="Cash Bag History"
                >
                  <FaInfo />
                </button>
              </span>

              <div
                style={{ display: "flex", gap: "0.5rem", marginBottom: "1rem" }}
              >
                {cashBag != null && cashBag > 0 && (
                  <button
                    className="game-icon-btn game-icon-btn--danger"
                    onClick={clearCashBag}
                  >
                    Clear
                  </button>
                )}

                {!currSession && cashBag != null && cashBag < 0 && (
                  <button
                    className="game-icon-btn game-icon-btn--danger"
                    onClick={() => setShowCashResetWarning(true)}
                  >
                    Reset
                  </button>
                )}

                {cashBag != null && (
                  <button
                    className="game-icon-btn game-icon-btn--success"
                    onClick={verifyCashAmount}
                  >
                    Verify Amount
                  </button>
                )}
              </div>

              <form className="game-form" onSubmit={addCashRefill}>
                <input
                  type="number"
                  className="game-input game-input--small"
                  min="1"
                  step="1"
                  placeholder="Refill"
                  value={refillAmount}
                  onWheel={preventNumberInputWheel}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === "") {
                      setRefillAmount("");
                      return;
                    }
                    const n = Number(v);
                    if (Number.isInteger(n) && n >= 1)
                      setRefillAmount(String(n));
                  }}
                />

                {isValidRefillInt && (
                  <select
                    className="game-input game-input--small"
                    value={refillWalletId}
                    onChange={(e) => setRefillWalletId(e.target.value)}
                    required
                  >
                    <option value="" hidden>
                      From
                    </option>
                    {wallets.map((w) => (
                      <option key={w.id} value={w.id}>
                        {w.name}
                      </option>
                    ))}
                  </select>
                )}

                {isValidRefillInt && refillWalletId && (
                  <button
                    type="submit"
                    className="game-btn game-btn--sm game-btn--primary"
                    disabled={isSubmitting}
                  >
                    <FaCheck />
                  </button>
                )}
              </form>
            </div>

            {/* Right Column - Session Stats */}
            <div className="wallets-session-card"
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "0.75rem",
                alignItems: "flex-end",
              }}
            >
              {chipsOnTables !== 0 && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: ".5rem",
                  }}
                >
                  <span className="game-text-secondary">
                    Chips {currSession ? "" : "Left"} on Tables:
                  </span>
                  <span
                    className={chipsOnTables < 0 ? "game-text-danger" : "game-text-primary"}
                    style={{ fontWeight: 600 }}
                  >
                    ${chipsOnTables}
                  </span>

                </div>
              )}

              {sessionRake > 0 && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: ".5rem",
                  }}
                >
                  <span className="game-text-secondary">
                    {currSession ? "" : "Last"} Session Rake:
                  </span>
                  <span
                    className="game-text-primary"
                    style={{ fontWeight: 600 }}
                  >
                    ${sessionRake}
                  </span>
                </div>
              )}

              {sessionTips > 0 && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: ".5rem",
                  }}
                >
                  <span className="game-text-secondary">
                    {currSession ? "" : "Last"} Session Tips:{" "}
                  </span>
                  <span
                    className="game-text-primary"
                    style={{ fontWeight: 600 }}
                  >
                    ${sessionTips}
                  </span>
                </div>
              )}

              {totalCredit > 0 && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: ".5rem",
                  }}
                >
                  <span className="game-text-secondary">
                    Total Session Credit:
                  </span>
                  <span
                    className="game-text-primary"
                    style={{ fontWeight: 600 }}
                  >
                    ${totalCredit}
                  </span>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Costs and Worker Transactions - Two Columns */}
        <div className="game-sidebar-grid">
          {/* Costs Section */}
          <section className="game-section">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "1rem",
              }}
            >
              <h2
                className="game-section__title"
                style={{ margin: 0, paddingBottom: 0, border: "none" }}
              >
                Costs
              </h2>
              <button
                className="game-icon-btn game-icon-btn--info"
                onClick={listCosts}
                title="View Costs"
              >
                <FaInfo />
              </button>
            </div>

            <form className="game-form-vertical" onSubmit={addCost}>
              <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                <input
                  type="number"
                  className="game-input"
                  style={{ flex: 1, margin: 0 }}
                  step="1"
                  min="0"
                  placeholder="Amount"
                  value={costAmount}
                  onWheel={preventNumberInputWheel}
                  onChange={(e) => setCostAmount(e.target.value)}
                  required
                />
                <label
                          style={{
                            display: "flex", alignItems: "center", gap: "0.5rem",
                            padding: "0.6rem 0.85rem",
                            border: `2px solid var(--game-border)`,
                            borderRadius: "var(--game-radius)", cursor: "pointer",
                            background: "var(--game-bg-tertiary)",
                            transition: "all 0.15s ease",
                            whiteSpace: "nowrap",
                          }}
                >
                  <input
                    type="checkbox"
                    checked={costPayLater}
                    onChange={(e) => {
                      setCostPayLater(e.target.checked);
                      if (e.target.checked) {
                        setCostMethod("");
                        setCostReceiver("");
                      }
                    }}
                    style={{ width: 16, height: 16, accentColor: "var(--game-primary)", cursor: "pointer" }}
                  />
                  Pay Later
                </label>
              </div>

              <input
                type="text"
                className="game-input"
                placeholder="Notes"
                value={costDescription}
                onChange={(e) => setCostDescription(e.target.value)}
                required
              />

              {!costPayLater && (
                <select
                  className="game-input"
                  value={costMethod}
                  onChange={(e) => {
                    setCostMethod(e.target.value);
                    if (["cash", ""].includes(e.target.value))
                      setCostReceiver("");
                  }}
                  required
                >
                  <option value="" hidden>
                    Payment Method
                  </option>
                  <option value="cash">Cash</option>
                  <option value="venmo">Venmo</option>
                  <option value="cashapp">CashApp</option>
                  <option value="bitcoin">Bitcoin</option>
                  <option value="zelle">Zelle</option>
                  <option value="applePay">Apple Pay</option>
                  <option value="paypal">PayPal</option>
                </select>
              )}

              {!costPayLater && !["cash", ""].includes(costMethod) && (
                <select
                  className="game-input"
                  value={costReceiver}
                  onChange={(e) => setCostReceiver(e.target.value)}
                  required
                >
                  <option value="" hidden>
                    From Wallet
                  </option>
                  {wallets.length > 0 ? (
                    wallets.map((w) => (
                      <option key={w.id} value={w.id}>
                        {w.name}
                      </option>
                    ))
                  ) : (
                    <option disabled>No wallets</option>
                  )}
                </select>
              )}

              <button
                type="submit"
                className="game-btn game-btn--primary"
                disabled={
                  isSubmitting ||
                  !costAmount ||
                  costAmount <= 0 ||
                  !costDescription.trim() ||
                  (!costPayLater && !costMethod) ||
                  (!costPayLater && !["cash"].includes(costMethod) && !costReceiver)
                }
              >
                Add Cost
              </button>
            </form>
          </section>

          {/* Worker Transactions Section */}
          <section className="game-section">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "1rem",
              }}
            >
              <h2
                className="game-section__title"
                style={{ margin: 0, paddingBottom: 0, border: "none" }}
              >
                Service Staff Tips
              </h2>
              <button
                className="game-icon-btn game-icon-btn--info"
                onClick={listAllPayouts}
              >
                <FaInfo />
              </button>
            </div>

            <form
              className="game-form-vertical"
              onSubmit={addWorkerTransaction}
            >
              <select
                className="game-input"
                value={selectedWorkerId}
                onChange={(e) => setSelectedWorkerId(e.target.value)}
                required
              >
                <option value="" hidden>
                  Select Worker
                </option>
                {workers.map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.name}
                  </option>
                ))}
              </select>

              <input
                type="number"
                className="game-input"
                step="1"
                min="0"
                placeholder="Amount"
                value={workerAmount}
                onWheel={preventNumberInputWheel}
                onChange={(e) => setWorkerAmount(e.target.value)}
                required
              />



              <select
                className="game-input"
                value={workerMethod}
                onChange={(e) => {
                  setWorkerMethod(e.target.value);
                  if (["cash", "house", ""].includes(e.target.value))
                    setWorkerReceiver("");
                }}
                required
              >
                <option value="" hidden>
                  Payment Method
                </option>
                <option value="cash">Cash</option>
                <option value="credit">Credit</option>
                <option value="venmo">Venmo</option>
                <option value="cashapp">CashApp</option>
                <option value="bitcoin">Bitcoin</option>
                <option value="zelle">Zelle</option>
                <option value="applePay">Apple Pay</option>
                <option value="paypal">PayPal</option>
              </select>

              {!["cash", "credit", ""].includes(workerMethod) && (
                <select
                  className="game-input"
                  value={workerReceiver}
                  onChange={(e) => setWorkerReceiver(e.target.value)}
                  required
                >
                  <option value="" hidden>
                    From Wallet
                  </option>
                  {wallets.length > 0 ? (
                    wallets.map((w) => (
                      <option key={w.id} value={w.id}>
                        {w.name}
                      </option>
                    ))
                  ) : (
                    <option disabled>No wallets</option>
                  )}
                </select>
              )}

              <button
                type="submit"
                className="game-btn game-btn--primary"
                disabled={
                  isSubmitting ||
                  !selectedWorkerId ||
                  !workerAmount ||
                  workerAmount <= 0 ||
                  !workerMethod ||
                  (!["cash", "credit"].includes(workerMethod) &&
                    !workerReceiver)
                }
              >
                Add Transaction
              </button>
            </form>
          </section>
        </div>

        {unpaidCosts.length > 0 && (
          <section className="game-section">
            <h2 className="game-section__title">Unpaid Costs</h2>
            <div className="game-transaction-list">
              {unpaidCosts.map((c) => (
                <div
                  key={c.id}
                  className="game-transaction-item"
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    border: "1px solid var(--game-border)",
                    padding: "1rem",
                    background: "var(--game-bg-secondary)",
                    gap: "1rem",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.75rem",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "baseline", gap: "0.75rem" }}>
                      <span
                        className="game-text-primary"
                        style={{ fontWeight: 600, fontSize: "1.1rem" }}
                      >
                        ${c.amount}
                      </span>
                      <span
                        className="game-text-secondary"
                        style={{ fontSize: "0.85rem" }}
                      >
                        {c.notes} •{" "}
                        {new Date(c.completed_at).toLocaleTimeString([], {
                          hour: "numeric",
                          minute: "2-digit",
                          hour12: true,
                        })}{" "}
                        •{" "}
                        {new Date(c.completed_at).getDate()}/
                        {new Date(c.completed_at).getMonth() + 1} by{" "}
                        {c.user_name}
                      </span>
                    </div>
                    <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                      <select
                        className="game-input game-input--small"
                        style={{ width: "120px" }}
                        value={payingUnpaidId === c.id ? payUnpaidMethod : ""}
                        onChange={(e) => {
                          setPayingUnpaidId(c.id);
                          setPayUnpaidMethod(e.target.value);
                          setPayUnpaidWalletId("");
                        }}
                      >
                        <option value="" hidden>
                          Method
                        </option>
                        <option value="cash">Cash</option>
                        <option value="venmo">Venmo</option>
                        <option value="cashapp">CashApp</option>
                        <option value="bitcoin">Bitcoin</option>
                        <option value="zelle">Zelle</option>
                        <option value="applePay">Apple Pay</option>
                        <option value="paypal">PayPal</option>
                      </select>

                      {payingUnpaidId === c.id &&
                        payUnpaidMethod &&
                        payUnpaidMethod !== "cash" && (
                          <select
                            className="game-input game-input--small"
                            style={{ width: "120px" }}
                            value={payUnpaidWalletId}
                            onChange={(e) =>
                              setPayUnpaidWalletId(e.target.value)
                            }
                          >
                            <option value="" hidden>
                              Wallet
                            </option>
                            {wallets.map((w) => (
                              <option key={w.id} value={w.id}>
                                {w.name}
                              </option>
                            ))}
                          </select>
                        )}

                      {payingUnpaidId === c.id &&
                        payUnpaidMethod &&
                        (payUnpaidMethod === "cash" || payUnpaidWalletId) && (
                          <button
                            className="game-btn game-btn--primary game-btn--sm"
                            disabled={isSubmitting}
                            onClick={() => payUnpaidCost(c.id)}
                          >
                            Pay
                          </button>
                        )}

                      {payingUnpaidId === c.id && payUnpaidMethod && (
                        <button
                          className="game-icon-btn game-icon-btn--secondary"
                          onClick={() => {
                            setPayingUnpaidId(null);
                            setPayUnpaidMethod("");
                            setPayUnpaidWalletId("");
                          }}
                          title="Clear selection"
                        >
                          <FaTimes />
                        </button>
                      )}
                    </div>
                  </div>

                  <button
                    className="game-icon-btn game-icon-btn--danger"
                    onClick={() => deleteUnpaidCost(c.id)}
                  >
                    <FaRegTrashAlt />
                  </button>
                </div>
              ))}
            </div>
          </section>
        )}


        {/* Wallets Section - Full Width */}
        <section className="game-section">
          <h2 className="game-section__title">Wallets</h2>

          <form
            className="game-form"
            onSubmit={addWallet}
            style={{ marginBottom: "1.5rem" }}
          >
            <input
              type="text"
              className="game-input"
              placeholder="Wallet name"
              value={newWalletName}
              onChange={(e) => setNewWalletName(e.target.value)}
              required
            />
            <button
              type="submit"
              className="game-btn game-btn--primary"
              disabled={
                isSubmitting || isWalletNameTaken || newWalletName.trim() === ""
              }
            >
              {isWalletNameTaken ? "Name Taken" : "Add Wallet"}
            </button>

            {totalWalletBalance !== 0 && (
              <div
                className="wallets-total-summary"
                style={{
                  padding: "0.75rem 1rem",
                  background: "var(--game-bg-tertiary)",
                  borderRadius: "var(--game-radius)",
                  border: "1px solid var(--game-border)",
                }}
              >
                <span
                  className="game-text-secondary"
                  style={{ marginRight: "0.5rem" }}
                >
                  {totalWalletBalance > 0
                    ? "Total Owed from Wallets:"
                    : "Total Owed to Wallets:"}
                </span>
                <span style={{ fontSize: "1.125rem", fontWeight: 700 }}>
                  ${Math.abs(totalWalletBalance)}
                </span>
              </div>
            )}
          </form>

          {wallets.length === 0 ? (
            <p className="game-empty">No wallets added yet</p>
          ) : (
            <ul className="game-list">
              {wallets.map((w) => {
                const total = w.digital || 0;

                return (
                  <li key={w.id} className="game-list-item">
                    <div
                      className="game-list-info"
                      onClick={() => listWalletTransactions(w.id)}
                    >
                      <span className="game-list-name">{w.name}</span>

                      {total !== 0 && (
                        <span
                          className={
                            total > 0
                              ? "game-player-badge game-player-badge--debt"
                              : "game-player-badge game-player-badge--credit"
                          }
                          style={{ fontSize: "0.85rem", width: "fit-content" }}
                        >
                          {total > 0
                            ? `Owes $${Math.abs(total)}`
                            : `Owed $${Math.abs(total)}`}
                        </span>
                      )}
                    </div>

                    <div className="game-player-actions">
                      <button
                        className="game-icon-btn game-icon-btn--info"
                        onClick={() => listWalletTransactions(w.id)}
                        title="Wallet History"
                      >
                        <FaInfo />
                      </button>

                      {permission?.includes("W") && (
                                              <button
                        className="game-icon-btn game-icon-btn--warning"
                        onClick={() => {
                          setPayingWallet(w);
                          setPayDirection(
                            total > 0 ? "from_wallet" : "to_wallet"
                          );
                          setPayWalletAmount("");
                          setPayWalletNotes("");
                          setReceiverWalletId("");
                        }}
                      >
                        <FaDollarSign />
                      </button>
                      )}

                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
          </>
        )}
      </div>

      {/* ============================================================================ */}
      {/* MODALS */}
      {/* ============================================================================ */}

      {showStartSessionWarning && (
        <>
          <div
            className="game-modal-overlay"
            onClick={() => !isSubmitting && setShowStartSessionWarning(false)}
          />
          <div className="game-modal">
            <div className="game-modal__header">
              <h3 className="game-modal__title" style={{ color: "#f6ad55" }}>
                Start Session Warning
              </h3>
              <button
                type="button"
                className="game-icon-btn"
                onClick={() => setShowStartSessionWarning(false)}
                disabled={isSubmitting}
                aria-label="Close start session warning"
              >
                <FaTimes />
              </button>
            </div>
            <div className="game-modal__body">
              <p style={{ color: "var(--game-text-primary)", fontSize: "0.95rem", margin: 0, lineHeight: 1.5 }}>
                Cash Bag is currently <strong>${cashBag}</strong>.
              </p>
              {Number(cashBag || 0) < 0 ? (
                <p style={{ color: "var(--game-text-secondary)", fontSize: "0.875rem", margin: "0.75rem 0 0", lineHeight: 1.5 }}>
                  It is recommended to reset the cash bag before starting a new session.
                </p>
              ) : (
                <p style={{ color: "var(--game-text-secondary)", fontSize: "0.875rem", margin: "0.75rem 0 0", lineHeight: 1.5 }}>
                  It is recommended to clear the cash bag before starting a new session.
                </p>
              )}
            </div>
            <div className="game-modal__footer">
              <button
                type="button"
                className="game-btn game-btn--ghost"
                onClick={() => setShowStartSessionWarning(false)}
                disabled={isSubmitting}
              >
                Review Cash Bag
              </button>
              <button
                type="button"
                className="game-btn game-btn--primary"
                onClick={confirmStartSession}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Starting..." : "Start Session Anyway"}
              </button>
            </div>
          </div>
        </>
      )}

      {showCashResetWarning && (
        <>
          <div
            className="game-modal-overlay"
            onClick={() => !isSubmitting && setShowCashResetWarning(false)}
          />
          <div className="game-modal">
            <div className="game-modal__header">
              <h3 className="game-modal__title" style={{ color: "#f56565" }}>
                Reset Cash Bag
              </h3>
              <button
                type="button"
                className="game-icon-btn"
                onClick={() => setShowCashResetWarning(false)}
                disabled={isSubmitting}
                aria-label="Close reset cash bag warning"
              >
                <FaTimes />
              </button>
            </div>
            <div className="game-modal__body">
              <p style={{ color: "var(--game-text-primary)", fontSize: "0.95rem", margin: 0, lineHeight: 1.5 }}>
                Cash Bag is currently <strong>${cashBag}</strong>.
              </p>
              <p style={{ color: "var(--game-text-secondary)", fontSize: "0.875rem", margin: "0.75rem 0 0", lineHeight: 1.5 }}>
                Refilling the cash bag is recommended because it records where the cash came from.
              </p>
              <p style={{ color: "var(--game-text-secondary)", fontSize: "0.875rem", margin: "0.75rem 0 0", lineHeight: 1.5 }}>
                A reset is a hard correction to zero. Use it only when you do not need to track the source of the cash or you intentionally want to clear the balance without a refill record.
              </p>
            </div>
            <div className="game-modal__footer">
              <button
                type="button"
                className="game-btn game-btn--ghost"
                onClick={() => setShowCashResetWarning(false)}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="game-btn game-btn--danger"
                onClick={resetNegativeCashBag}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Resetting..." : "Hard Reset Cash Bag"}
              </button>
            </div>
          </div>
        </>
      )}

      {showEndSessionWarning && (
        <>
          <div
            className="game-modal-overlay"
            onClick={() => !isSubmitting && setShowEndSessionWarning(false)}
          />
          <div className="game-modal">
            <div className="game-modal__header">
              <h3 className="game-modal__title" style={{ color: "#f6ad55" }}>
                End Session Warning
              </h3>
              <button
                type="button"
                className="game-icon-btn"
                onClick={() => setShowEndSessionWarning(false)}
                disabled={isSubmitting}
                aria-label="Close end session warning"
              >
                <FaTimes />
              </button>
            </div>
            <div className="game-modal__body">
              <p style={{ color: "var(--game-text-primary)", fontSize: "0.95rem", margin: 0, lineHeight: 1.5 }}>
                You are about to end the session while session totals are not cleared.
              </p>
              {Number(cashBag || 0) !== 0 && (
                <p style={{ color: "var(--game-text-secondary)", fontSize: "0.875rem", margin: "0.75rem 0 0", lineHeight: 1.5 }}>
                  Cash Bag is currently <strong>${cashBag}</strong>. It is recommended to clear the cash bag before ending the session.
                </p>
              )}
              {Number(chipsOnTables || 0) !== 0 && (
                <p style={{ color: "var(--game-text-secondary)", fontSize: "0.875rem", margin: "0.75rem 0 0", lineHeight: 1.5 }}>
                  Chips on Tables is currently <strong>${chipsOnTables}</strong>. This may mean not all chips have been collected or something was missed.
                </p>
              )}
            </div>
            <div className="game-modal__footer">
              <button
                type="button"
                className="game-btn game-btn--ghost"
                onClick={() => setShowEndSessionWarning(false)}
                disabled={isSubmitting}
              >
                Review Session
              </button>
              <button
                type="button"
                className="game-btn game-btn--danger"
                onClick={confirmEndSession}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Ending..." : "End Session Anyway"}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Wallet Payment Modal */}
      {payingWallet && (
        <>
          <div
            className="game-modal-overlay"
            onClick={() => setPayingWallet(null)}
          />
          <div className="game-modal game-modal--transaction">
            <div className="game-modal__header">
              <h3 className="game-modal__title">
                Payment: {payingWallet.name}
              </h3>
              {payingWallet.digital !== 0 && (
                <span
                  className={
                    payingWallet.digital > 0
                      ? "game-badge game-badge--debt"
                      : "game-badge game-badge--credit"
                  }
                >
                  {payingWallet.digital > 0
                    ? `Owes $${Math.abs(payingWallet.digital)}`
                    : `Owed $${Math.abs(payingWallet.digital)}`}
                </span>
              )}
            </div>

            <div className="game-modal__body">
              <form
                className="game-form-vertical"
                onSubmit={handleWalletPayment}
              >
                {/* Direction Toggle */}
                <div
                  style={{
                    overflowX: "auto",
                    WebkitOverflowScrolling: "touch",
                    marginBottom: "0.5rem",
                  }}
                >
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr 1fr",
                      gap: "0.5rem",
                      minWidth: "360px",
                    }}
                  >
                    <button
                      type="button"
                      className={`game-btn ${
                        payDirection === "to_wallet"
                          ? "game-btn--primary"
                          : "game-btn--secondary"
                      }`}
                      onClick={() => setPayDirection("to_wallet")}
                    >
                      House → Wallet
                    </button>
                    <button
                      type="button"
                      className={`game-btn ${
                        payDirection === "from_wallet"
                          ? "game-btn--primary"
                          : "game-btn--secondary"
                      }`}
                      onClick={() => setPayDirection("from_wallet")}
                    >
                      Wallet → House
                    </button>
                    <button
                      type="button"
                      className={`game-btn ${
                        payDirection === "wallet_to_wallet"
                          ? "game-btn--primary"
                          : "game-btn--secondary"
                      }`}
                      onClick={() => setPayDirection("wallet_to_wallet")}
                    >
                      Wallet → Wallet
                    </button>
                  </div>
                </div>

                {/* Visual Direction Indicator */}
                <div
                  style={{
                    padding: "1rem",
                    background: "var(--game-bg-tertiary)",
                    borderRadius: "var(--game-radius)",
                    marginBottom: "1rem",
                    textAlign: "center",
                    fontWeight: 600,
                    color:
                      payDirection === "to_wallet"
                        ? "var(--game-success)"
                        : payDirection === "from_wallet" 
                        ? "var(--game-danger)"
                        : "var(--game-session-yellow)",
                  }}
                >
                  {payDirection === "to_wallet" ? (
                    <>🏠 House paying → 💳 {payingWallet.name}</>
                  ) : payDirection === "from_wallet" ? (
                    <>💳 {payingWallet.name} paying → 🏠 House</>
                  ) : (
                    <>💳 {payingWallet.name} paying → 💳 Another Wallet</>
                  )}
                </div>

                {payDirection === "wallet_to_wallet" && (
                  <div style={{ marginBottom: "1rem" }}>
                    <Select
                      isSearchable
                      classNamePrefix="select"
                      placeholder="Select Receiving Wallet..."
                      options={wallets
                        .filter((w) => w.id !== payingWallet.id)
                        .map((w) => ({ value: w.id, label: w.name }))}
                      value={
                        receiverWalletId
                          ? wallets
                              .filter((w) => w.id !== payingWallet.id)
                              .map((w) => ({ value: w.id, label: w.name }))
                              .find((o) => o.value === parseInt(receiverWalletId)) || null
                          : null
                      }
                      onChange={(option) => setReceiverWalletId(option ? String(option.value) : "")}
                      styles={{
                        control: (base) => ({
                          ...base,
                          backgroundColor: "#2d3748",
                          borderColor: "#4a5568",
                        }),
                        menu: (base) => ({
                          ...base,
                          backgroundColor: "#2d3748",
                          zIndex: 9999,
                        }),
                        option: (base, { isFocused }) => ({
                          ...base,
                          backgroundColor: isFocused ? "#4a5568" : "#2d3748",
                          color: "#e2e8f0",
                        }),
                        singleValue: (base) => ({
                          ...base,
                          color: "#e2e8f0",
                        }),
                        input: (base) => ({
                          ...base,
                          color: "#e2e8f0",
                        }),
                        placeholder: (base) => ({
                          ...base,
                          color: "#718096",
                        }),
                      }}
                    />
                  </div>
                )}

                {/* Amount */}
                <input
                  type="number"
                  className="game-input"
                  step="1"
                  min="0"
                  placeholder="Amount"
                  ref={payWalletAmountInputRef}
                  value={payWalletAmount}
                  onWheel={preventNumberInputWheel}
                  onChange={(e) => setPayWalletAmount(e.target.value)}
                  required
                />

                {/* Notes */}
                <textarea
                  className="game-input"
                  placeholder="Notes (optional)"
                  value={payWalletNotes}
                  onChange={(e) => setPayWalletNotes(e.target.value)}
                  rows={3}
                  style={{ resize: "vertical", minHeight: "80px" }}
                />

                {/* Summary */}
                {payWalletAmount && (
                  <div
                    style={{
                      padding: "0.75rem",
                      background: "var(--game-bg-tertiary)",
                      borderRadius: "var(--game-radius)",
                      fontSize: "0.875rem",
                      color: "var(--game-text-secondary)",
                    }}
                  >
                    {payDirection === "to_wallet" ? (
                      <>
                        New balance:{" "}
                        <strong style={{ color: "var(--game-text-primary)" }}>
                          {payingWallet.digital + parseFloat(payWalletAmount) > 0 ? 'Owes ' : `Owed `}
                            ${Math.abs(payingWallet.digital + parseFloat(payWalletAmount))}
                        </strong>
                      </>
                    ) : (
                      <>
                        New balance:{" "}
                        <strong style={{ color: "var(--game-text-primary)" }}>
                          {payingWallet.digital - parseFloat(payWalletAmount) > 0 ? 'Owes ' : `Owed `}
                            ${Math.abs(payingWallet.digital - parseFloat(payWalletAmount))}
                        </strong>
                      </>
                    )}
                  </div>
                )}

                {/* Actions */}
                <div className="game-modal__footer">
                  <button
                    type="button"
                    className="game-btn game-btn--ghost"
                    onClick={() => setPayingWallet(null)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="game-btn game-btn--primary"
                    disabled={
                      isSubmitting ||
                      !payWalletAmount ||
                      parseFloat(payWalletAmount) <= 0
                    }
                  >
                    Submit
                  </button>
                </div>
              </form>
            </div>
          </div>
        </>
      )}

      {/* All Payouts Modal */}
      {showPayoutsModal && (
        <>
          <div
            className="game-modal-overlay"
            onClick={() => setShowPayoutsModal(false)}
          />
          <div className="game-modal game-modal--large">
            <div className="game-modal__header">
              <h3 className="game-modal__title">
                Staff Transactions 
              </h3>
              <button
                className="game-icon-btn"
                onClick={() => setShowPayoutsModal(false)}
              >
                <FaTimes />
              </button>
            </div>

            <div className="game-modal__body game-modal__body--scroll">
              {allPayouts.length === 0 ? (
                <p className="game-empty">No payouts yet.</p>
              ) : (
                <div className="game-transaction-list">
                  {allPayouts.map((p, idx) => {
                    const amt = parseFloat(p.amount).toFixed(0);

                    return (
                      <div
                        key={idx}
                        className="game-transaction-item"
                        style={
                          [28, 29].includes(p.code)
                            ? {
                                background: "rgba(237, 137, 54, 0.1)",
                                border: "1px solid rgba(237, 137, 54, 0.3)",
                              }
                            : {}
                        }
                      >
                        <span className="game-transaction-time">
                          {new Date(p.completed_at).toLocaleTimeString(
                            undefined,
                            {
                              hour: "numeric",
                              minute: "2-digit",
                            }
                          )}
                        </span>

                        <strong className="game-transaction-player">
                          {p.worker_name}
                        </strong>

                        <span className="game-transaction-amount">${amt}</span>

                        {p.code === 25 ? (
                          <span className="game-transaction-method">
                            credit
                          </span>
                        ) : (
                          <span className="game-transaction-method">
                            {` ${p.method}${
                              p.wallet_name ? (parseFloat(p.amount) > 0 ? " to " : " from ") + p.wallet_name : ""
                            }`}
                          </span>
                        )}

                        {[28, 29].includes(p.code) && (
                          <span
                            className="game-badge game-badge--running"
                            style={{ marginLeft: "auto" }}
                          >
                            Payout from Balance
                          </span>
                        )}

                        <span className="game-transaction-date">
                          {new Date(p.completed_at).toLocaleDateString(
                            undefined,
                            {
                              month: "numeric",
                              day: "numeric",
                            }
                          )}
                        </span>
                        <button
                          className="game-icon-btn game-icon-btn--sm game-icon-btn--danger"
                          disabled={!currSession}
                          title={currSession ? "" : "Can only delete from active session"}
                          onClick={() =>
                            deleteTransaction(
                              p.id,
                              p.code,
                              "staff_payout",
                              p.service_staff_id,
                              p.amount,
                              p.wallet_name
                            )
                          }
                        >
                          <FaRegTrashAlt />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Costs Modal */}
      {showCostsModal && (
        <>
          <div
            className="game-modal-overlay"
            onClick={() => setShowCostsModal(false)}
          />
          <div className="game-modal game-modal--large">
            <div className="game-modal__header">
              <h3 className="game-modal__title">Session Costs</h3>
              <button
                className="game-icon-btn"
                onClick={() => setShowCostsModal(false)}
              >
                <FaTimes />
              </button>
            </div>

            <div className="game-modal__body game-modal__body--scroll">
              {costs.length === 0 ? (
                <p className="game-empty">
                  {currSession
                    ? "No costs for this session yet"
                    : "No active session"}
                </p>
              ) : (
                <div className="game-transaction-list">
                  {costs.map((c) => (
                    <div key={c.id} className="game-transaction-item">
                      <span className="game-transaction-time">
                        {new Date(c.completed_at).toLocaleTimeString(
                          undefined,
                          {
                            hour: "numeric",
                            minute: "2-digit",
                          }
                        )}
                      </span>
                      <span className="game-transaction-amount">
                        ${parseFloat(c.amount).toFixed(0)}
                      </span>
                      <span className="game-transaction-type">{c.notes}</span>
                      <span className="game-transaction-method">
                        {c.method}
                        {c.wallet_name ? (parseFloat(c.amount) > 0 ? " from " + c.wallet_name : " to " + c.wallet_name) : ""}
                      </span>
                      <span className="game-transaction-date">
                        {new Date(c.completed_at).toLocaleDateString(
                          undefined,
                          {
                            month: "numeric",
                            day: "numeric",
                          }
                        )}
                      </span>
                        <button
                            className="game-icon-btn game-icon-btn--sm game-icon-btn--danger"
                                                      disabled={!currSession}
                          title={currSession ? "" : "Can only delete from active session"}
                            onClick={() =>
                              deleteTransaction(
                                c.id,
                                32,
                                "cost",
                                null,
                                c.amount,
                                c.wallet_name
                              )
                            }
                          >
                            <FaRegTrashAlt />
                          </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Cash Bag Transactions Modal */}
      {showCashBagModal && (
        <>
          <div
            className="game-modal-overlay"
            onClick={() => setShowCashBagModal(false)}
          />
          <div className="game-modal game-modal--large">
            <div className="game-modal__header">
              <h3 className="game-modal__title">Cash Bag Transactions</h3>
              <button
                className="game-icon-btn"
                onClick={() => setShowCashBagModal(false)}
              >
                <FaTimes />
              </button>
            </div>

            <div className="game-modal__body game-modal__body--scroll">
              {cashBagTransactions.length === 0 ? (
                <p className="game-empty">No transactions yet.</p>
              ) : (
                <div className="game-transaction-list">
                  {cashBagTransactions.map((t) => {
                    const isVerify = t.type === "verify";
                    const isRefill = t.type === "refill";
                    const isClear = t.type === "clear";
                    const amt = parseFloat(t.amount).toFixed(0);

                    return (
                      <div
                        key={`${t.type}-${t.id}`}
                        className="game-transaction-item"
                        style={
                          isVerify
                            ? {
                                background: "rgba(72, 187, 120, 0.1)",
                                border: "1px solid rgba(72, 187, 120, 0.3)",
                              }
                            : isRefill
                            ? {
                                background: "rgba(66, 153, 225, 0.1)",
                                border: "1px solid rgba(66, 153, 225, 0.3)",
                              }
                            : isClear
                            ? {
                                background: "rgba(245, 101, 101, 0.1)",
                                border: "1px solid rgba(245, 101, 101, 0.3)",
                              }
                            : {}
                        }
                      >
                        <span className="game-transaction-time">
                          {new Date(t.completed_at).toLocaleTimeString(
                            undefined,
                            {
                              hour: "numeric",
                              minute: "2-digit",
                            }
                          )}
                        </span>

                        <strong className="game-transaction-player">
                          {t.name}
                        </strong>

                        <span className="game-transaction-amount">
                          ${isVerify ? amt : Math.abs(amt)}
                        </span>

                        {isVerify ? (
                          <span
                            className="game-badge game-badge--credit"
                            title="Cash amount verification"
                          >
                            verification
                          </span>
                        ) : isRefill ? (
                          <span
                            className="game-badge game-badge--running"
                            title="Cash refill from wallet"
                          >
                            refill
                          </span>
                        ) : isClear ? (
                          <span
                            className="game-badge game-badge--debt"
                            title="Cash bag cleared"
                          >
                            cleared
                          </span>
                        ) : (
                          <>
                            <span className="game-transaction-type">
                              {formatTransactionCode(t.code, t.type, t.method)}
                            </span>
                            {t.table_name && ( // ADD THIS
                              <span className="game-transaction-table">
                                {t.table_name}
                              </span>
                            )}
                          </>
                        )}

                        <span className="game-transaction-date">
                          {new Date(t.completed_at).toLocaleDateString(
                            undefined,
                            {
                              month: "numeric",
                              day: "numeric",
                            }
                          )}
                        </span>

                        {(isVerify || isRefill || isClear) && (
                          <button
                            className="game-icon-btn game-icon-btn--sm game-icon-btn--danger"
                                                      disabled={!currSession}
                          title={currSession ? "" : "Can only delete from active session"}
                            onClick={() =>
                              deleteTransaction(
                                t.id,
                                t.type === 'refill' ? 33 : t.type === 'verify' ? 34 : 35,
                                t.type,
                                null,
                                t.amount,
                                t.name
                              )
                            }
                          >
                            <FaRegTrashAlt />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </>
      )}

     {/* Wallet Transactions Modal */}
{selectedWalletId !== null && (
  <>
    <div
      className="game-modal-overlay"
      onClick={() => {
        setSelectedWalletId(null);
        setSelectedWalletTransactions([]);
      }}
    />
    <div className="game-modal game-modal--large">
      <div className="game-modal__header">
        <h3 className="game-modal__title">
          {wallets.find((w) => w.id === selectedWalletId)?.name || "Wallet"} Transactions
        </h3>
        <button
          className="game-icon-btn"
          onClick={() => {
            setSelectedWalletId(null);
            setSelectedWalletTransactions([]);
          }}
        >
          <FaTimes />
        </button>
      </div>

      <div className="game-modal__body game-modal__body--scroll">
        {selectedWalletTransactions.length === 0 ? (
          <p className="game-empty">No transactions yet.</p>
        ) : (
          <div className="game-transaction-list">
            {selectedWalletTransactions.map((t, idx) => {
              const amt = parseFloat(t.amount).toFixed(0);
              
              // Determine if this is a wallet payment (special styling)
              const isWalletPayment = t.source === 'wallet_payment';
              const isWalletToWallet = isWalletPayment && (t.entity_name?.startsWith('Sent to ') || t.entity_name?.startsWith('Received from '));
              const isToWallet = isWalletPayment && t.amount > 0;
              const isFromWallet = isWalletPayment && t.amount < 0;

              return (
                <div 
                  key={`${t.source}-${t.id}`} 
                  className="game-transaction-item"
                  style={isWalletPayment ? {
                    background: isToWallet 
                      ? "rgba(72, 187, 120, 0.1)" 
                      : "rgba(245, 101, 101, 0.1)",
                    border: isToWallet
                      ? "1px solid rgba(72, 187, 120, 0.3)"
                      : "1px solid rgba(245, 101, 101, 0.3)",
                  } : {}}
                >
                  <span className="game-transaction-time">
                    {new Date(t.completed_at).toLocaleTimeString(undefined, {
                      hour: "numeric",
                      minute: "2-digit",
                    })}
                  </span>

                  <span className="game-transaction-amount">${Math.abs(amt)}</span>

                  {/* Entity name (player, dealer, staff, or description) */}
                  {t.entity_name && (
                    <strong className="game-transaction-player">
                      {t.entity_name}
                    </strong>
                  )}

                  {/* Transaction type/source */}
                  <span className="game-transaction-type">
                    {t.source === 'wallet_payment' 
                      ? (isWalletToWallet ? 'Wallet → Wallet' : isToWallet ? 'House → Wallet' : 'Wallet → House')
                      : formatTransactionCode(t.code, t.source)}
                  </span>

                  {/* Method (if applicable) */}
                  {t.method && !isWalletPayment && (
                    <span className="game-transaction-method">
                      {t.method}
                    </span>
                  )}

                  {/* Table name (if applicable) */}
                  <span className="game-transaction-table">
                    {t.table_id ? (t.table_name || `Table id: ${t.table_id}`) : null}
                  </span>

                  {/* Settlement badge (if applicable) */}
                  {t.settlement === 1 && (
                    <span 
                      className="game-badge game-badge--running" 
                      style={{ marginLeft: "auto" }}
                    >
                      Settlement
                    </span>
                  )}

                  {/* Wallet payment badge (if applicable) */}
                  {isWalletPayment && (
                    <span 
                      className={`game-badge ${isToWallet ? 'game-badge--credit' : 'game-badge--debt'}`}
                      style={{ marginLeft: t.settlement === 1 ? "0" : "auto" }}
                    >
                      {isToWallet ? 'Received' : 'Sent'}
                    </span>
                  )}

                  <span className="game-transaction-date">
                    {new Date(t.completed_at).toLocaleDateString(undefined, {
                      month: "numeric",
                      day: "numeric",
                    })}
                  </span>

                  {isWalletPayment && (
                        <button
      className="game-icon-btn game-icon-btn--sm game-icon-btn--danger"
                                disabled={!currSession}
                          title={currSession ? "" : "Can only delete from active session"}
      onClick={() =>
        deleteTransaction(
          t.id,
          36,
          "wallet_payment",
          null,
          t.amount,
          wallets.find(w => w.id === selectedWalletId)?.name
        )
      }
    >
      <FaRegTrashAlt />
    </button>

                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  </>
)}
    </div>
  );
}
